import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import Select from 'react-select';
import { Button, Col, FormGroup, Input, Modal, Row } from 'reactstrap';
import { axiosGet, axiosPost } from 'util/apiConfig.js';

const DURATIONS = [30, 45, 50, 60].map((minutes) => ({ value: minutes, label: `${minutes} minutes` }));
const TIME_ZONES = moment.tz.names().map((name) => ({ value: name, label: name }));

const emptyForm = {
  teacherId: '',
  classId: '',
  name: '',
  email: '',
  mobile: '',
  startDate: '',
  startTime: '',
  durationMinutes: 45,
  localTimeZone: 'America/New_York',
};

// Books a trial class for a teacher by hand (same record a NeetoCal booking creates). The API
// refuses a time that overlaps the teacher's classes or trials.
const AddTrialModal = ({ isOpen, onClose, onSaved, classList }) => {
  const [teachers, setTeachers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setForm(emptyForm);
    setError('');
    axiosGet('teacher/list-of-all-teacher').then((response) => {
      if (!response.isError) {
        setTeachers((response.data?.result || []).map((teacher) => ({ value: teacher.teacherId, label: teacher.name })));
      }
    });
  }, [isOpen]);

  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const save = () => {
    const missing = ['teacherId', 'classId', 'name', 'email', 'startDate', 'startTime'].filter((field) => !form[field]);
    if (missing.length) {
      setError('Please fill in teacher, class, lead name, lead email, date and time.');
      return;
    }
    setSaving(true);
    setError('');
    axiosPost('sales/admin-add-trial', {
      ...form,
      // <input type="time"> gives 24-hour "HH:mm"; the API takes the local time as "hh:mm A".
      startTime: moment(form.startTime, 'HH:mm').format('hh:mm A'),
    })
      .then((response) => {
        setSaving(false);
        if (response.isError) {
          setError(response.message || 'Something went wrong!');
          return;
        }
        onSaved();
      })
      .catch((errorResponse) => {
        setSaving(false);
        setError(errorResponse?.message || 'Something went wrong!');
      });
  };

  const classOptions = (classList || []).map((item) => ({ value: item.classId, label: item.name }));

  return (
    <Modal className="modal-dialog-centered" isOpen={isOpen} toggle={onClose}>
      <div className="modal-header">
        <h5 className="modal-title">Add Trial Class</h5>
        <button aria-label="Close" className="close" type="button" onClick={onClose}>
          <span aria-hidden>×</span>
        </button>
      </div>
      <div className="modal-body">
        <FormGroup>
          <label className="form-control-label">Teacher</label>
          <Select
            options={teachers}
            placeholder="Select teacher"
            isSearchable
            value={teachers.find((option) => option.value === form.teacherId) || null}
            onChange={(option) => set('teacherId', option ? option.value : '')}
          />
        </FormGroup>
        <FormGroup>
          <label className="form-control-label">Class</label>
          <Select
            options={classOptions}
            placeholder="Select class"
            isSearchable
            value={classOptions.find((option) => option.value === form.classId) || null}
            onChange={(option) => set('classId', option ? option.value : '')}
          />
        </FormGroup>
        <Row>
          <Col md="6">
            <FormGroup>
              <label className="form-control-label">Lead name</label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <label className="form-control-label">Lead email</label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <label className="form-control-label">Lead mobile (optional)</label>
          <Input value={form.mobile} onChange={(e) => set('mobile', e.target.value)} />
        </FormGroup>
        <Row>
          <Col md="6">
            <FormGroup>
              <label className="form-control-label">Date</label>
              <Input type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <label className="form-control-label">Start time</label>
              <Input type="time" value={form.startTime} onChange={(e) => set('startTime', e.target.value)} />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <FormGroup>
              <label className="form-control-label">Duration</label>
              <Select
                options={DURATIONS}
                value={DURATIONS.find((option) => option.value === form.durationMinutes)}
                onChange={(option) => set('durationMinutes', option.value)}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <label className="form-control-label">Time zone</label>
              <Select
                options={TIME_ZONES}
                isSearchable
                value={TIME_ZONES.find((option) => option.value === form.localTimeZone)}
                onChange={(option) => set('localTimeZone', option.value)}
              />
            </FormGroup>
          </Col>
        </Row>
        {error && <div className="text-danger font-weight-bold">{error}</div>}
      </div>
      <div className="modal-footer">
        <Button color="secondary" type="button" onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Button color="primary" type="button" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Add Trial'}
        </Button>
      </div>
    </Modal>
  );
};

export default AddTrialModal;
